/**
 * @author jakubbujakowski
 * @since 2020-7-6
 */

import * as React from "react";
import styled, { css } from "styled-components";
import { Colors } from "@batmaid/design-tokens";

import theme from "@ui/themes/default";
import { Info } from "@ui/Assets/Symbolicons/Info";
import { Warning } from "@ui/Assets/Symbolicons/Warning";
import { TestWrapper } from "@ui/Atoms/TestWrapper";

export type Color = "red" | "black" | "grey" | "blue";
type Icon = "info" | "warning" | "none";

interface Props {
  message: string | JSX.Element;
  background?: keyof Colors;
  color?: Color;
  icon?: Icon;
  testId?: string;
  className?: string;
  withBorder?: boolean;
}

const getColorFromTheme = (color: Color) => {
  switch (color) {
    case "red":
      return theme.palette.red700;
    case "black":
      return theme.palette.grey800;
    case "blue":
      return theme.palette.blue700;
    default:
      return theme.palette.grey600;
  }
};

const InfoMessage = ({
  color = "grey",
  icon = "info",
  ...props
}: Props): React.ReactElement => (
  <TestWrapper testId={props.testId}>
    <InfoMessageContainer
      background={props.background}
      color={getColorFromTheme(color)}
      className={props.className}
      withBorder={props.withBorder}
    >
      {icon === "info" && (
        <Info
          size={24}
          height={24}
          variant="line"
          color={getColorFromTheme(color)}
        />
      )}
      {icon === "warning" && (
        <Warning
          size={24}
          height={24}
          variant="solid"
          color={theme.palette.red700}
        />
      )}
      {props.message}
    </InfoMessageContainer>
  </TestWrapper>
);

InfoMessage.displayName = "InfoMessage";

export { InfoMessage };

const InfoMessageContainer = styled.div<{
  color: string;
  background?: keyof Colors;
  withBorder?: boolean;
}>`
  display: flex;
  align-items: self-start;
  color: ${props => props.color};
  font-size: ${props => props.theme.fonts.sizes.body2};

  svg {
    flex-shrink: 0;
    margin-right: ${props => props.theme.margins.base};
    margin-left: -${props => props.theme.margins.half};
  }

  ${props =>
    props.withBorder &&
    css`
      border: 1px solid ${props.theme.palette.grey100};
      border-radius: ${props.theme.border.radius.base};
      padding: ${props.theme.margins.base};
    `}

  ${props =>
    props.background &&
    css`
      background-color: ${props.theme.palette[props.background]};
    `}
`;
